import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofEdge } from "building-types/roofs/roofEdgeTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import type {
  ConnectedRoofPerimeterRailFilter,
  RoofPerimeterWallRail,
  RoofPerimeterRail,
  RoofRailConerInfo,
} from "building-types/roofs/roofPerimeterTypes";
import {
  RoofRailEndCondition,
  RoofPerimeterRailCondition,
  RoofPerimeterRailType,
  RoofRailCornerType,
} from "building-types/roofs/roofPerimeterTypes";
import { ROOF_RAIL_COLLINEARITY_TOLERANCE, ROOF_RAIL_RTREE_BOX_HALF_WIDTH } from "building-types/roofs/roofTrimTypes";
import type { EvaluatedRoofSettings } from "building-types/settings/roofSettings";
import { type RoofExtensions, type ShedRoofExtensions } from "building-types/settings/roofTypes";
import {
  EaveType,
  WrapFriezeBoardMode,
  EaveCutType,
  RakeFriezeBoardMode,
} from "building-types/settings/settingsTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import { FASCIA_THICKNESS, FASCIA_TOP_TRIM_SIZE } from "common/constants/roofTrimConstants";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import uid from "common/utils/uid";
import { DEFAULT_EAVE_RETURN_DISTANCE } from "doc/settings/defaultSettings";
import { supportsEaveCutType } from "doc/settingsTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import { aabb2ByTwoPoints, aabb2Grow } from "geom/aabb2";
import { aabb3ByTwoPoints } from "geom/aabb3";
import { addConvexPlanesToBSPLeaf } from "geom/bsp";
import type { ClipRailContext } from "geom/bspClipping";
import { clipRail3, clippingNodeNew } from "geom/bspClipping";
import { type PlaneBSP } from "geom/bspTypes";
import { Vec2PolyLine2ContainmentClass, classifyContainmentVec2PolyLine2 } from "geom/containment";
import { COMPACTNESS_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED, Line2Side, ZERO_EPSILON } from "geom/core";
import { CurveCurvePointIntersection, intersectLine2Line2 } from "geom/intersectLine2Line2";
import { joinPolyLine3Rails } from "geom/join";
import { line2Collinear, line2Side } from "geom/line2";
import { offsetPolyLine3VariableInXYPlane } from "geom/offset";
import {
  planeIsParallel,
  planeNew,
  planeNewXY,
  planeNormal,
  planeProject,
  planeProjectPolyLine3,
  planeReverse,
} from "geom/plane";
import { planeBSPByPointAndNormal, planeBSPMoveAlongNormal } from "geom/planeBSP";
import {
  polyLine2AABB2,
  polyLine2Copy,
  polyLine2EdgeEnd,
  polyLine2EdgeStart,
  polyLine2EdgeTangent,
  polyLine2End,
  polyLine2Extend,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2Start,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3Concat,
  polyLine3Copy,
  polyLine3Edge,
  polyLine3EdgeCount,
  polyLine3EdgeLength,
  polyLine3EdgeSlopeInZ,
  polyLine3EdgeTangent,
  polyLine3EndPoint,
  polyLine3EvalEdge,
  polyLine3ExtendBothEnds,
  polyLine3ExtendEnd,
  polyLine3ExtendStart,
  polyLine3Length,
  polyLine3Move,
  polyLine3New,
  polyLine3OnPlane,
  polyLine3Simplify,
  polyLine3StartPoint,
} from "geom/polyLine3";
import { pushXYZ } from "geom/utils";
import { vec2DistSquared, vec2Dot } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import {
  vec3Add,
  vec3AngleBetween,
  vec3Copy,
  vec3Cross,
  vec3DistSquared,
  vec3Dot,
  vec3Lerp,
  vec3Negate,
  vec3Normalize,
} from "geom/vec3";
import RBush from "rbush";
import type { BBox } from "rbush-3d";
import { RBush3D } from "rbush-3d";
import { includeElementBasedOnOrientation } from "../multiGableRoofTools";
import { roofPerimeterWallRailNew, roofPerimeterRailNew, roofRailCornerInfoNew } from "./roofPerimeters";

const ROOF_HEIGHT_AT_POINT_EPSILON = 0.1;
const ROOF_HEIGHT_AT_POINT_DOT_EPSILON = 0.01;

const hasSoffit: { [key in EaveType]?: boolean } = {
  [EaveType.BOXED_BOX]: true,
  [EaveType.BOXED_OPEN]: true,
  [EaveType.BOXED_MINI_HIP]: true,
  [EaveType.BOXED_MINI_SHED]: true,
  [EaveType.BOXED_FULL_RETURN]: true,
  [EaveType.ANGLED_ENCLOSED_OPEN]: true,
};

const hasEaveFasciaBoard: { [key in EaveType]?: boolean } = {
  [EaveType.BOXED_BOX]: true,
  [EaveType.BOXED_OPEN]: true,
  [EaveType.BOXED_MINI_HIP]: true,
  [EaveType.BOXED_MINI_SHED]: true,
  [EaveType.BOXED_FULL_RETURN]: true,
  [EaveType.ANGLED_ENCLOSED_OPEN]: true,
};

const hasRakeFasciaBoard: { [key in EaveType]?: boolean } = {
  [EaveType.BOXED_BOX]: true,
  [EaveType.BOXED_OPEN]: true,
  [EaveType.BOXED_MINI_HIP]: true,
  [EaveType.BOXED_MINI_SHED]: true,
  [EaveType.BOXED_FULL_RETURN]: true,
  [EaveType.ANGLED_ENCLOSED_OPEN]: true,
  [EaveType.ANGLED_OPEN_OPEN]: true,
};

const hasFriezeBoard: { [key in EaveType]?: boolean } = {
  [EaveType.BOXED_BOX]: true,
  [EaveType.BOXED_OPEN]: true,
  [EaveType.BOXED_MINI_HIP]: true,
  [EaveType.BOXED_MINI_SHED]: true,
  [EaveType.BOXED_FULL_RETURN]: true,
  [EaveType.ANGLED_ENCLOSED_OPEN]: true,
};

const hasExposedRafters: { [key in EaveType]?: boolean } = {
  [EaveType.ANGLED_OPEN_OPEN]: true,
};

const MIN_ROOF_PERIMETER_RAIL_LENGTH = 0.1;
const ROOF_RAIL_COINCIDENT_EPSILON = 0.1;

interface ClipRoofPerimeterRailOptions {
  readonly subRoof: GeneratedRoof;
  readonly roofRail: RoofPerimeterRail;
  readonly clipOptions: ClipRailContext;
  readonly outRoofRails?: RoofPerimeterRail[];
  readonly newEndCondition?: RoofPerimeterRailCondition;
  readonly offset?: number;
  readonly minLength?: number;
  readonly reconnect?: boolean;
}

export function clipRoofPerimeterRail({
  subRoof,
  roofRail,
  clipOptions,
  outRoofRails = [],
  newEndCondition = RoofPerimeterRailCondition.NONE,
  offset = 0,
  minLength = MIN_ROOF_PERIMETER_RAIL_LENGTH,
  reconnect = false,
}: ClipRoofPerimeterRailOptions): RoofPerimeterRail[] {
  const {
    roofVolume: { unclippedRoofTopFaces },
    rails,
  } = subRoof;

  const { peakRail } = rails;

  const {
    rail,
    rafterDirections,
    startCondition,
    endCondition,
    wrapFriezeBoardModeAtStart,
    wrapFriezeBoardModeAtEnd,
    eaveCutTypeAtStart,
    eaveCutTypeAtEnd,
  } = roofRail;

  const edgeCount = polyLine3EdgeCount(rail);

  if (!(edgeCount === rafterDirections.length)) {
    console.assert(false);
  }

  // the extra extension is used to detect new clipping conditions on
  // already clipped roof perimeter rails
  const extraExtension = 2 * MULTIGABLE_BSP_EPSILON;

  const extendedRail = polyLine3ExtendBothEnds(polyLine3Copy(rail), extraExtension);
  const [sx, sy, sz] = polyLine3StartPoint(extendedRail);
  const [ex, ey, ez] = polyLine3EndPoint(extendedRail);

  // helper functions for determining if the clipped rail is the start or end of the original rail
  const isStartRail = (subRail: PolyLine3) => {
    const [x, y, z] = polyLine3StartPoint(subRail);
    return vec3DistSquared(sx, sy, sz, x, y, z) < DIST_EPSILON_SQUARED;
  };

  const isEndRail = (subRail: PolyLine3) => {
    const [x, y, z] = polyLine3EndPoint(subRail);
    return vec3DistSquared(ex, ey, ez, x, y, z) < DIST_EPSILON_SQUARED;
  };

  // iterate segments
  let clippedRails: PolyLine3[] = [];
  for (let i = 0; i < edgeCount; i++) {
    const rafterDirection = rafterDirections[i];
    const subRail = polyLine3Edge(extendedRail, i);

    // move rail out
    polyLine3Move(subRail, rafterDirection[0] * offset, rafterDirection[1] * offset, rafterDirection[2] * offset);

    // clip
    let clippedSubRails = clipRail3({ rail: subRail, options: clipOptions });

    // move results back
    clippedSubRails = clippedSubRails.map((x) =>
      polyLine3Move(x, -rafterDirection[0] * offset, -rafterDirection[1] * offset, -rafterDirection[2] * offset),
    );

    clippedRails.push(...clippedSubRails);
  }

  // join
  clippedRails = joinPolyLine3Rails({
    segments: clippedRails,
    simplify: true,
    allowRevert: false,
  });

  // filter
  clippedRails = clippedRails.filter((x) => polyLine3Length(x) > 2 * ROOF_RAIL_RTREE_BOX_HALF_WIDTH);

  // filter out rails that are too short
  clippedRails = clippedRails.filter((x) => {
    return polyLine3Length(x) > minLength;
  });

  // create new roof rails with the clipped rails
  outRoofRails.push(
    ...clippedRails.map((clippedRail) => {
      const isStart = isStartRail(clippedRail);
      const isEnd = isEndRail(clippedRail);

      const normals = buildRoofPerimeterRailNormals(clippedRail, unclippedRoofTopFaces);
      const rafterDirections = buildRoofPerimeterRailRafterDirections(clippedRail, normals, peakRail);

      if (isStart && polyLine3EdgeLength(clippedRail, 0) > extraExtension) {
        polyLine3ExtendStart(clippedRail, -extraExtension);
      }

      if (isEnd && polyLine3EdgeLength(clippedRail, polyLine3EdgeCount(clippedRail) - 1) > extraExtension) {
        polyLine3ExtendEnd(clippedRail, -extraExtension);
      }

      return {
        ...roofRail,
        normals,
        id: uid(),
        rafterDirections,
        startCondition: isStart ? startCondition : newEndCondition,
        endCondition: isEnd ? endCondition : newEndCondition,
        prevPerimeterRail: undefined,
        nextPerimeterRail: undefined,
        wrapFriezeBoardModeAtStart: isStart ? wrapFriezeBoardModeAtStart : WrapFriezeBoardMode.NONE,
        wrapFriezeBoardModeAtEnd: isEnd ? wrapFriezeBoardModeAtEnd : WrapFriezeBoardMode.NONE,
        hasSquaredReturnAtStart: isStart && eaveCutTypeAtStart === RoofRailEndCondition.SQUARED,
        hasSquaredReturnAtEnd: isEnd && eaveCutTypeAtEnd === RoofRailEndCondition.SQUARED,
        rail: clippedRail,
      };
    }),
  );

  if (reconnect) {
    outRoofRails = reconnectRoofPerimeterRails(outRoofRails);
  }

  return outRoofRails;
}

function reconnectRoofPerimeterRails(
  roofPerimeterRails: RoofPerimeterRail[],
  rtree: RBush3D | null = null,
  boxHalfSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
): RoofPerimeterRail[] {
  // build rtree if needed
  if (!rtree) {
    rtree = buildRoofPerimeterRailsRTree(roofPerimeterRails, boxHalfSize);
  }

  const outRoofPerimeterRails: RoofPerimeterRail[] = [];
  for (const roofPerimeterRail of roofPerimeterRails) {
    const prevPerimeterRails = getConnectedRoofPerimeterRails(roofPerimeterRail, rtree, false);
    const nextPerimeterRails = getConnectedRoofPerimeterRails(roofPerimeterRail, rtree, true);

    outRoofPerimeterRails.push({
      ...roofPerimeterRail,
      prevPerimeterRail: prevPerimeterRails.length ? prevPerimeterRails[0] : undefined,
      nextPerimeterRail: nextPerimeterRails.length ? nextPerimeterRails[0] : undefined,
    });
  }

  return outRoofPerimeterRails;
}

export function reconnectRoofPerimeterRailsTable(
  roofPerimeterRailsTable: Table<RoofPerimeterRail[]>,
  rtree: RBush3D | null = null,
  boxHalfSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
): Table<RoofPerimeterRail[]> {
  // build rtree if needed
  if (!rtree) {
    rtree = buildRoofPerimeterRailsTableRTree(roofPerimeterRailsTable, boxHalfSize);
  }

  const outRoofPerimeterRailsTable: RWTable<RoofPerimeterRail[]> = {};
  for (const subRoofId in roofPerimeterRailsTable) {
    const roofPerimeterRails = roofPerimeterRailsTable[subRoofId];
    outRoofPerimeterRailsTable[subRoofId] = reconnectRoofPerimeterRails(roofPerimeterRails, rtree, boxHalfSize);
  }

  return outRoofPerimeterRailsTable;
}

interface ClipRoofPerimeterRailsOptions {
  readonly subRoof: GeneratedRoof;
  readonly roofRails: RoofPerimeterRail[];
  readonly clipOptions: ClipRailContext;
  readonly outRoofRails?: RoofPerimeterRail[];
  readonly newEndCondition?: RoofPerimeterRailCondition;
  readonly offset?: number;
  readonly reconnect?: boolean;
}

export function clipRoofPerimeterRails({
  subRoof,
  roofRails,
  clipOptions,
  outRoofRails = [],
  newEndCondition = RoofPerimeterRailCondition.NONE,
  offset = 0,
  reconnect = false,
}: ClipRoofPerimeterRailsOptions): RoofPerimeterRail[] {
  for (const roofRail of roofRails) {
    clipRoofPerimeterRail({ subRoof, roofRail, clipOptions, outRoofRails, newEndCondition, offset, reconnect });
  }

  return outRoofRails;
}

export function rakeExtensionAtCoplanarWallFilter(roofRails: RoofPerimeterRail, isEnd: boolean): boolean {
  const { rail, startCondition, endCondition, type, prevPerimeterRail, nextPerimeterRail } = roofRails;
  if (!rail || type !== RoofPerimeterRailType.RAKE) {
    return false;
  }
  const condition = isEnd ? endCondition : startCondition;

  const slope = polyLine3EdgeSlopeInZ(rail, isEnd ? polyLine3EdgeCount(rail) - 1 : 0);

  const endValidForExtension = isEnd ? !nextPerimeterRail && slope > 0 : !prevPerimeterRail && slope < 0;

  return condition === RoofPerimeterRailCondition.COPLANAR_WALL && endValidForExtension;
}

export function coplanarWallExtensionFilter(roofRails: RoofPerimeterRail, isEnd: boolean): boolean {
  const { rail, startCondition, endCondition } = roofRails;
  if (!rail) {
    return false;
  }
  const condition = isEnd ? endCondition : startCondition;
  const slope = Math.abs(polyLine3EdgeSlopeInZ(rail, isEnd ? polyLine3EdgeCount(rail) - 1 : 0));

  return condition === RoofPerimeterRailCondition.COPLANAR_WALL && slope < DIST_EPSILON;
}

export function nonCoplanarRakeExtensionFilter(roofRails: RoofPerimeterRail, isEnd: boolean): boolean {
  const { rail, startCondition, endCondition } = roofRails;
  if (!rail) {
    return false;
  }
  const condition = isEnd ? endCondition : startCondition;
  const slope = Math.abs(polyLine3EdgeSlopeInZ(rail, isEnd ? polyLine3EdgeCount(rail) - 1 : 0));

  return condition === RoofPerimeterRailCondition.NON_COPLANAR_RAKE && slope < DIST_EPSILON;
}

export function applyRoofPerimeterRailExtension(
  roofRail: RoofPerimeterRail,
  extension: number,
  applySlopeCorrection?: boolean,
  filter?: (roofRails: RoofPerimeterRail, isEnd: boolean) => boolean,
): RoofPerimeterRail {
  if (extension < DIST_EPSILON) {
    return roofRail;
  }

  let { appliedStartExtension, appliedEndExtension } = roofRail;
  const { eaveCutTypeAtStart, eaveCutTypeAtEnd } = roofRail;

  const rail = polyLine3Copy(roofRail.rail);
  let slopeCorrectionFactorStart = 1,
    slopeCorrectionFactorEnd = 1;

  if (applySlopeCorrection) {
    const slopeAtStart = polyLine3EdgeSlopeInZ(rail, 0);
    const slopeAtEnd = polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1);

    slopeCorrectionFactorStart = Math.sqrt(1 + slopeAtStart ** 2);
    slopeCorrectionFactorEnd = Math.sqrt(1 + slopeAtEnd ** 2);
  }

  if (!filter || filter(roofRail, false)) {
    polyLine3ExtendStart(rail, extension * slopeCorrectionFactorStart);
    appliedStartExtension += extension;
  }

  if (!filter || filter(roofRail, true)) {
    polyLine3ExtendEnd(rail, extension * slopeCorrectionFactorEnd);
    appliedEndExtension += extension;
  }

  return {
    ...roofRail,
    rail,
    appliedStartExtension,
    appliedEndExtension,
    eaveCutTypeAtStart:
      eaveCutTypeAtStart === RoofRailEndCondition.SQUARED && appliedStartExtension < DIST_EPSILON
        ? RoofRailEndCondition.SQUARED
        : eaveCutTypeAtStart,
    eaveCutTypeAtEnd:
      eaveCutTypeAtEnd === RoofRailEndCondition.SQUARED && appliedEndExtension < DIST_EPSILON
        ? RoofRailEndCondition.SQUARED
        : eaveCutTypeAtEnd,
  };
}

export function applyRoofPerimeterRailsExtension(
  roofRails: RoofPerimeterRail[],
  extension: number,
  applySlopeCorrection?: boolean,
  filter?: (roofRails: RoofPerimeterRail, isEnd: boolean) => boolean,
): RoofPerimeterRail[] {
  const outRails: RoofPerimeterRail[] = [];

  for (const roofRail of roofRails) {
    outRails.push(applyRoofPerimeterRailExtension(roofRail, extension, applySlopeCorrection, filter));
  }

  return outRails;
}

export function getRoofHeightAtPoint(roofTopFaces: RoofFace[], x: number, y: number, defaultZ: number = 0): number {
  let minZ = Number.MAX_VALUE;
  let found = false;

  for (const { face } of roofTopFaces) {
    const { plane, loops } = face;
    const [nx, ny, nz] = planeNormal(plane);

    // ignore faces that are not facing up
    if (vec3Dot(nx, ny, nz, 0, 0, 1) < ZERO_EPSILON) {
      continue;
    }

    const d = vec3Dot(plane.originX, plane.originY, plane.originZ, nx, ny, nz);
    const t = (d - vec3Dot(nx, ny, nz, x, y, 0)) / vec3Dot(nx, ny, nz, 0, 0, 1);
    const [px, py, pz] = vec3Lerp(x, y, 0, x, y, 1, t);
    const [u, v] = planeProject(plane, px, py, pz);
    if (
      classifyContainmentVec2PolyLine2(
        u,
        v,
        loops[0],
        ROOF_HEIGHT_AT_POINT_EPSILON,
        ROOF_HEIGHT_AT_POINT_DOT_EPSILON,
      ) !== Vec2PolyLine2ContainmentClass.OUTSIDE
    ) {
      minZ = Math.min(minZ, pz);
      found = true;
    }
  }

  return found ? minZ : defaultZ;
}

function getRoofNormalAtPoint(roofTopFaces: RoofFace[], x: number, y: number, defaultNormal: Vec3 = [0, 0, 1]): Vec3 {
  let normal: Vec3 | undefined = undefined;
  let minZ = Number.MAX_VALUE;

  for (const { face } of roofTopFaces) {
    const { plane, loops } = face;
    const [nx, ny, nz] = planeNormal(plane);

    // ignore faces that are not facing up
    if (vec3Dot(nx, ny, nz, 0, 0, 1) < ZERO_EPSILON) {
      continue;
    }

    const d = vec3Dot(plane.originX, plane.originY, plane.originZ, nx, ny, nz);
    const t = (d - vec3Dot(nx, ny, nz, x, y, 0)) / vec3Dot(nx, ny, nz, 0, 0, 1);
    const [px, py, pz] = vec3Lerp(x, y, 0, x, y, 1, t);
    const [u, v] = planeProject(plane, px, py, pz);
    if (
      classifyContainmentVec2PolyLine2(u, v, loops[0], DIST_EPSILON, DIST_EPSILON) !==
      Vec2PolyLine2ContainmentClass.OUTSIDE
    ) {
      if (pz < minZ) {
        minZ = pz;
        normal = vec3Copy(planeNormal(plane));
      }
    }
  }

  return normal ?? defaultNormal;
}

interface BuildRoofPerimeterRailsOptions {
  readonly subRoofId: string;
  readonly exactEdges: RoofEdge[];
  readonly edges: RoofEdge[];
  readonly roofSettings: EvaluatedRoofSettings;
  readonly wallSettings: WallSettings;
  readonly extensions: RoofExtensions;
  readonly shedExtensions: ShedRoofExtensions;
  readonly roofTopFaces: RoofFace[];
  readonly peakRail: PolyLine3;
  readonly isDormer: boolean;
}

/**
 * This function builds the perimeter rails for a gable roof, by projecting the
 * 2D roof perimeter onto the 3D roof faces.
 *  1.- We create an RTree with the edges of the roof faces
 *  2.- We iterate the 2D roof perimeter edges and look for intersections with the
 *      roof faces edges, using the RTree for fast lookup.
 *  3.- We split the 2D roof perimeter edges at the intersections
 *  4.- We project the resulting 2D edges onto the 3D roof faces and create the
 *      corresponding roof perimeter rail passing all the needed properties from
 *      the source edge
 *  5.- Finally we detect the connectivity between the rails by looking for coincident
 *      vertices
 */
export function buildRoofPerimeterRails({
  subRoofId,
  exactEdges,
  edges,
  roofSettings,
  wallSettings,
  extensions,
  shedExtensions,
  roofTopFaces,
  peakRail,
  isDormer,
}: BuildRoofPerimeterRailsOptions): RoofPerimeterRail[] {
  interface RBox {
    readonly minX: number;
    readonly minY: number;
    readonly maxX: number;
    readonly maxY: number;
    readonly line: PolyLine2;
  }

  const roofPerimeterRails: RoofPerimeterRail[] = [];

  const {
    eaveFasciaSize: eaveFasciaSizeFromRoofSettings,
    rakeFasciaSize: rakeFasciaSizeFromRoofSettings,
    soffitOffset: soffitOffsetFromRoofSettings = 0,
    eaveType,
    eaveCutType: settingsEaveCutType = EaveCutType.PLUMB,
    eaveReturnDistance = DEFAULT_EAVE_RETURN_DISTANCE,
    rakeFasciaMaterial: rakeFasciaMaterialFromRoofSettings,
    eaveFasciaMaterial: eaveFasciaMaterialFromRoofSettings,
    fasciaTopTrimMaterial: fasciaTopTrimMaterialFromRoofSettings,
    soffitMaterial: soffitMaterialFromRoofSettings,
    eaveOverhang,
    rakeOverhang,
    rakeFriezeBoardExtents = RakeFriezeBoardMode.WALL_AND_SOFFIT,
    miniHipOrientation = StreetOrientationMode.FRONT_FACES,
    fullReturnOrientation = StreetOrientationMode.FRONT_FACES,
    overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
  } = roofSettings;

  const { hasStartExtension, hasEndExtension, hasStartPartialOverlap, hasEndPartialOverlap } = extensions;

  const { hasLeftExtension, hasRightExtension } = shedExtensions;

  const exteriorWallOffsetWithSheathing = getExteriorWallOffset(
    wallSettings,
    WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE,
  );

  const exteriorWallOffsetFromOverhangDatum = getExteriorWallOffset(wallSettings, overhangDatum);

  const rtree = new RBush<RBox>();

  // helper functions
  const isValidEdge = ({ isStartEdge, isEndEdge, isLeftEdge, isRightEdge }: RoofEdge) => {
    return (
      (!isStartEdge || !(hasStartExtension && !hasStartPartialOverlap)) &&
      (!isEndEdge || !(hasEndExtension && !hasEndPartialOverlap)) &&
      (!isLeftEdge || !hasLeftExtension) &&
      (!isRightEdge || !hasRightExtension)
    );
  };

  const hasExtension = ({ isStartEdge, isEndEdge, isLeftEdge, isRightEdge }: RoofEdge) => {
    return (
      (isStartEdge && hasStartExtension) ||
      (isEndEdge && hasEndExtension) ||
      (isLeftEdge && hasLeftExtension) ||
      (isRightEdge && hasRightExtension)
    );
  };

  // helper function for adding a split line to RTree
  const addSplitLine = (line: PolyLine2) => {
    const aabb = aabb2Grow(polyLine2AABB2(line), DIST_EPSILON, DIST_EPSILON);
    rtree.insert({
      ...aabb,
      line,
    });
  };

  // helper function for building the wall limits
  const buildWallLimits = ({ edge }: RoofEdge, prevEdgeInfo: RoofEdge, nextEdgeInfo: RoofEdge): PolyLine3 => {
    const innerEdge = polyLine2Copy(edge);

    if (hasExtension(prevEdgeInfo)) {
      polyLine2ExtendStart(innerEdge, 10000); // TODO: use a value obtained from the roof extension
    }

    if (hasExtension(nextEdgeInfo)) {
      polyLine2ExtendEnd(innerEdge, 10000); // TODO: use a value obtained from the roof extension
    }

    return innerEdge;
  };

  // helper function for building a 3D rail
  const buildEdge3 = (
    { outerEdge, startHeight, endHeight }: RoofEdge,
    prevEdgeInfo: RoofEdge,
    nextEdgeInfo: RoofEdge,
    isBaseRail: boolean,
  ): PolyLine3 => {
    const edge = polyLine2Copy(outerEdge);
    const hasStartExtension = hasExtension(prevEdgeInfo);
    const hasEndExtension = hasExtension(nextEdgeInfo);

    if (hasStartExtension) {
      polyLine2ExtendStart(edge, 10000); // TODO: use a value obtained from the roof extension
    }

    if (hasEndExtension) {
      polyLine2ExtendEnd(edge, 10000); // TODO: use a value obtained from the roof extension
    }

    const [x0, y0] = polyLine2Start(edge);
    const [x1, y1] = polyLine2End(edge);

    const points: number[] = [];

    const addPoint = (x: number, y: number, defaultZ: number = Number.NEGATIVE_INFINITY) => {
      const z = getRoofHeightAtPoint(roofTopFaces, x, y, defaultZ);

      if (z === Number.NEGATIVE_INFINITY) {
        return;
      }

      pushXYZ(points, x, y, z);
    };

    // start point
    addPoint(x0, y0, isBaseRail && !hasStartExtension ? startHeight : Number.NEGATIVE_INFINITY);

    // look for intersections using the RTree
    const intersectionPoints: number[][] = [];
    const searchAABB = aabb2Grow(aabb2ByTwoPoints(x0, y0, x1, y1), DIST_EPSILON, DIST_EPSILON);
    const splitLines = rtree.search(searchAABB);

    for (let j = 0, lj = splitLines.length; j < lj; j++) {
      const { line } = splitLines[j];
      const [ax, ay] = polyLine2EdgeStart(line, 0);
      const [bx, by] = polyLine2EdgeEnd(line, 0);

      const int = intersectLine2Line2(x0, y0, x1, y1, ax, ay, bx, by, DIST_EPSILON, DIST_EPSILON);

      if (int && int instanceof CurveCurvePointIntersection) {
        intersectionPoints.push([int.x, int.y, vec2Dot(int.x - x0, int.y - y0, x1 - x0, y1 - y0)]);
      }
    }

    // sort the intersection (by distance to the start point)
    intersectionPoints.sort((a: number[], b: number[]) => a[2] - b[2]);

    // insert the sorted intersections
    for (const intersectionPoint of intersectionPoints) {
      addPoint(intersectionPoint[0], intersectionPoint[1]);
    }

    // end point
    addPoint(x1, y1, isBaseRail && !hasEndExtension ? endHeight : Number.NEGATIVE_INFINITY);

    const pointsToPolyLine3 = polyLine3New(points);
    // we only simplify the rail if it has more than equal to 6 points
    if (pointsToPolyLine3.length >= 6) {
      const newEdge3 = polyLine3Simplify(pointsToPolyLine3, ROOF_RAIL_COINCIDENT_EPSILON);

      //if after simplification the rail has less than 6 points we return the original rail
      //the idea is to always have a rail with at least one segment
      return newEdge3.length >= 6
        ? newEdge3
        : polyLine3New([
            points[0],
            points[1],
            points[2],
            points[points.length - 3],
            points[points.length - 2],
            points[points.length - 1],
          ]);
    } else {
      return pointsToPolyLine3;
    }
  };

  // build an RTree with the potential split lines
  const groundPlane = planeNewXY();
  for (let i = 0, l = roofTopFaces.length; i < l; i++) {
    const { face } = roofTopFaces[i];
    const { loops, plane } = face;

    if (!loops?.length || !plane) {
      continue;
    }

    const pl = polyLine3OnPlane(loops[0], plane);
    const edgeCount = pl ? polyLine3EdgeCount(pl) : 0;
    for (let j = 0; j < edgeCount; j++) {
      const edge = polyLine3Edge(pl, j);
      const line = planeProjectPolyLine3(groundPlane, edge);
      polyLine2Extend(line, ROOF_HEIGHT_AT_POINT_EPSILON, ROOF_HEIGHT_AT_POINT_EPSILON);

      addSplitLine(line);
    }
  }

  // iterate edges for building roof rails
  for (let i = 0, l = exactEdges.length; i < l; i++) {
    const roofEdge = exactEdges[i];
    const prevRoofEdge = exactEdges[(i + l - 1) % l];
    const nextRoofEdge = exactEdges[(i + 1) % l];

    const {
      isRake,
      sideOverhang,
      outerEdge,
      edge: baseEdge,
      side,
      isStartEdge,
      isEndEdge,
      fullyTruncated,
      exteriorWallOffsetWithFinish,
      roofMaterialTrimAndProps,
      roofEdgeOverrides,
      eaveCutType: edgeCutType,
    } = roofEdge;

    assertIsNotNullOrUndefined(roofMaterialTrimAndProps);

    const {
      includeFasciaTopTrim: includeFasciaTopTrimFromEdgeOverride,
      fasciaTopTrimSize = FASCIA_TOP_TRIM_SIZE,
      friezeBoard,
      friezeBoardMaterialLegacy,
      wrapFriezeBoardMode = WrapFriezeBoardMode.NONE,
      fasciaTopTrimMaterial: fasciaTopTrimMaterialFromEdgeOverride,
      soffitMaterial: soffitMaterialFromEdgeOverride,
      fasciaMaterial: fasciaMaterialFromEdgeOverride,
      fasciaSize: fasciaSizeFromEdgeOverride,
      soffitOffset: soffitOffsetFromEdgeOverride = 0,
    } = roofMaterialTrimAndProps;

    // discard extension edges
    if (!isValidEdge(roofEdge)) {
      continue;
    }

    const fasciaTopTrimMaterial = fasciaTopTrimMaterialFromEdgeOverride ?? fasciaTopTrimMaterialFromRoofSettings;

    const adjustFasciaBoardSize = roofEdgeOverrides?.fasciaSize === undefined;

    const fasciaBoardSize = isRake
      ? fasciaSizeFromEdgeOverride ?? rakeFasciaSizeFromRoofSettings
      : fasciaSizeFromEdgeOverride ?? eaveFasciaSizeFromRoofSettings;

    // build the edge plane
    const [startX, startY] = polyLine2Start(outerEdge);
    const [dirX, dirY] = polyLine2EdgeTangent(outerEdge, 0);
    const edgePlane = planeNew(startX, startY, 0, dirX, dirY, 0, 0, 0, 1);

    // build the edge rail
    const wallLimitsRail = buildWallLimits(roofEdge, prevRoofEdge, nextRoofEdge);
    const rail = buildEdge3(roofEdge, prevRoofEdge, nextRoofEdge, false);
    const baseRail = buildEdge3(roofEdge, prevRoofEdge, nextRoofEdge, true);

    if (!rail || polyLine3EdgeCount(rail) < 1) {
      continue;
    }

    const roofEdgeStartOverhang = roofEdge.startOverhang;
    const roofEdgeEndOverhang = roofEdge.endOverhang;

    // set the edge types
    const type = isRake && !fullyTruncated ? RoofPerimeterRailType.RAKE : RoofPerimeterRailType.EAVE;

    // do not include the fascia board if its size is zero
    const includeFasciaBoard =
      (isRake ? hasRakeFasciaBoard[eaveType] : hasEaveFasciaBoard[eaveType]) && fasciaBoardSize > DIST_EPSILON;

    // do not include the fascia top trim if its size is zero and if the fascia board is not included
    const includeFasciaTopTrim =
      includeFasciaTopTrimFromEdgeOverride && includeFasciaBoard && fasciaTopTrimSize > DIST_EPSILON;

    // include soffit based on eave type
    const includeSoffit = hasSoffit[eaveType];

    // build the base rafter directions
    const baseNormals = buildRoofPerimeterRailNormals(baseRail, roofTopFaces);
    const baseRafterDirections = buildRoofPerimeterRailRafterDirections(baseRail, baseNormals, peakRail);

    // build the edge normals
    const normals = buildRoofPerimeterRailNormals(rail, roofTopFaces);
    const rafterDirections = buildRoofPerimeterRailRafterDirections(rail, normals, peakRail);

    const fasciaBoardThickness = isRake
      ? Math.min(rakeOverhang - COMPACTNESS_EPSILON, FASCIA_THICKNESS)
      : Math.min(eaveOverhang - COMPACTNESS_EPSILON, FASCIA_THICKNESS); //@TO-DO: add rake fascia thickness
    const includeExposedRafters = hasExposedRafters[eaveType];

    // detect eave returns
    let eaveCutTypeAtStart: RoofRailEndCondition = RoofRailEndCondition.EXTENDED;
    let eaveCutTypeAtEnd: RoofRailEndCondition = RoofRailEndCondition.EXTENDED;

    const resolvedEaveCutType = supportsEaveCutType[eaveType] ? edgeCutType ?? settingsEaveCutType : EaveCutType.PLUMB;

    // looks for edges from the bounding perimeter, that are coincident with the current edge
    const refEdgeInfo = edges.find((x) => x.side === side);
    if (refEdgeInfo) {
      const refEdge = refEdgeInfo.edge;

      const [refX0, refY0] = polyLine2Start(refEdge);
      const [refX1, refY1] = polyLine2End(refEdge);

      const [x0, y0] = polyLine2Start(baseEdge);
      const [x1, y1] = polyLine2End(baseEdge);

      const supportsReturnAtEnd = vec2DistSquared(refX0, refY0, x0, y0) < DIST_EPSILON_SQUARED;
      const supportsReturnAtStart = vec2DistSquared(refX1, refY1, x1, y1) < DIST_EPSILON_SQUARED;

      const hasEaveReturn =
        (eaveType === EaveType.BOXED_FULL_RETURN &&
          includeElementBasedOnOrientation(edgePlane, fullReturnOrientation)) ||
        (eaveType === EaveType.BOXED_MINI_SHED && includeElementBasedOnOrientation(edgePlane, miniHipOrientation)) ||
        (eaveType === EaveType.BOXED_MINI_HIP && includeElementBasedOnOrientation(edgePlane, miniHipOrientation)) ||
        eaveType === EaveType.BOXED_BOX;

      function getRoofRailEndCondition(eaveCutType: EaveCutType): RoofRailEndCondition {
        switch (eaveCutType) {
          case EaveCutType.SQUARED:
            return RoofRailEndCondition.SQUARED;
          //a user issue was detected when the eave cut type was set to "INHERIT" so creating a default case which will return the plumb condition
          default:
          case EaveCutType.PLUMB:
            return RoofRailEndCondition.PLUMB;
        }
      }

      if (supportsReturnAtStart && hasEaveReturn) {
        eaveCutTypeAtStart = getRoofRailEndCondition(resolvedEaveCutType);
      }
      if (supportsReturnAtEnd && hasEaveReturn) {
        eaveCutTypeAtEnd = getRoofRailEndCondition(resolvedEaveCutType);
      }
    }

    // build the roof rail
    roofPerimeterRails.push(
      roofPerimeterRailNew({
        type,
        isDormer,
        subRoofId,
        edgeIndex: i,
        side,
        isStartEdge,
        isEndEdge,
        edgePlane,
        baseEdge,
        rail,
        baseRail,
        wallLimitsRail,
        normals,
        baseNormals,
        rafterDirections,
        baseRafterDirections,
        eaveType,
        eaveCutType: resolvedEaveCutType,
        eaveReturnDistance,
        exteriorWallOffsetWithSheathing,
        exteriorWallOffsetWithFinish,
        exteriorWallOffsetFromOverhangDatum,
        overhang: sideOverhang,
        roofEdgeStartOverhang,
        roofEdgeEndOverhang,
        includeExposedRafters: includeExposedRafters,
        includeFasciaBoard: includeFasciaBoard,
        fasciaBoardSize,
        adjustFasciaBoardSize,
        fasciaMaterial: isRake
          ? fasciaMaterialFromEdgeOverride ?? rakeFasciaMaterialFromRoofSettings
          : fasciaMaterialFromEdgeOverride ?? eaveFasciaMaterialFromRoofSettings,
        includeSoffit: includeSoffit,
        soffitOffset: Math.max(soffitOffsetFromEdgeOverride ?? soffitOffsetFromRoofSettings, 0),
        friezeBoard: hasFriezeBoard[eaveType] ? friezeBoard : [],
        friezeBoardMaterialLegacy,
        wrapFriezeBoardModeAtStart: isRake ? wrapFriezeBoardMode : WrapFriezeBoardMode.NONE,
        wrapFriezeBoardModeAtEnd: isRake ? wrapFriezeBoardMode : WrapFriezeBoardMode.NONE,
        rakeFriezeBoardExtents,
        includeFasciaTopTrim: includeFasciaTopTrim,
        fasciaTopTrimSize,
        fasciaTopTrimMaterial,
        soffitMaterial: soffitMaterialFromEdgeOverride ?? soffitMaterialFromRoofSettings,
        fasciaBoardThickness,
        eaveCutTypeAtStart,
        eaveCutTypeAtEnd,
      }),
    );
  }

  // create links
  for (let i = 0, l = roofPerimeterRails.length; i < l; i++) {
    const prevI = (i + l - 1) % l;
    const nextI = (i + 1) % l;

    const perimeterRail = roofPerimeterRails[i];
    const prevPerimeterRail = roofPerimeterRails[prevI];
    const nextPerimeterRail = roofPerimeterRails[nextI];

    const [startX, startY, startZ] = polyLine3StartPoint(perimeterRail.rail);
    const [endX, endY, endZ] = polyLine3EndPoint(perimeterRail.rail);

    const [prevEndX, prevEndY, prevEndZ] = polyLine3EndPoint(prevPerimeterRail.rail);
    const [nextStartX, nextStartY, nextStartZ] = polyLine3StartPoint(nextPerimeterRail.rail);

    if (vec3DistSquared(startX, startY, startZ, prevEndX, prevEndY, prevEndZ) < DIST_EPSILON_SQUARED) {
      roofPerimeterRails[prevI] = {
        ...prevPerimeterRail,
        nextPerimeterRail: perimeterRail,
      };

      roofPerimeterRails[i] = {
        ...perimeterRail,
        prevPerimeterRail,
      };
    }

    if (vec3DistSquared(endX, endY, endZ, nextStartX, nextStartY, nextStartZ) < DIST_EPSILON_SQUARED) {
      roofPerimeterRails[nextI] = {
        ...nextPerimeterRail,
        prevPerimeterRail: perimeterRail,
      };

      roofPerimeterRails[i] = {
        ...perimeterRail,
        nextPerimeterRail,
      };
    }
  }

  return roofPerimeterRails;
}

interface RoofPerimeterRailBox extends BBox {
  readonly roofPerimeterRail: RoofPerimeterRail;
  readonly isEnd: boolean;
}

function roofPerimeterRailBoxNew(
  bbox: BBox,
  roofPerimeterRail: RoofPerimeterRail,
  isEnd: boolean,
): RoofPerimeterRailBox {
  return {
    ...bbox,
    roofPerimeterRail,
    isEnd,
  };
}

export function buildRoofPerimeterRailsRTree(
  roofPerimeterRails: RoofPerimeterRail[],
  boxHalfSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
  rtree: RBush3D = new RBush3D(),
): RBush3D {
  const addEndPoint = (roofPerimeterRail: RoofPerimeterRail, isEnd: boolean) => {
    const { rail } = roofPerimeterRail;
    const [x, y, z] = isEnd ? polyLine3EndPoint(rail) : polyLine3StartPoint(rail);

    const bbox = aabb3ByTwoPoints(
      x - boxHalfSize,
      y - boxHalfSize,
      z - boxHalfSize,
      x + boxHalfSize,
      y + boxHalfSize,
      z + boxHalfSize,
    );

    rtree.insert(roofPerimeterRailBoxNew(bbox, roofPerimeterRail, isEnd));
  };

  for (const roofPerimeterRail of roofPerimeterRails) {
    addEndPoint(roofPerimeterRail, false);
    addEndPoint(roofPerimeterRail, true);
  }

  return rtree;
}

export function buildRoofPerimeterRailsTableRTree(
  roofPerimeterRailsTable: Table<RoofPerimeterRail[]>,
  boxHalfSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
  rtree: RBush3D = new RBush3D(),
): RBush3D {
  for (const subRoofId in roofPerimeterRailsTable) {
    const roofPerimeterRails = roofPerimeterRailsTable[subRoofId];

    buildRoofPerimeterRailsRTree(roofPerimeterRails, boxHalfSize, rtree);
  }

  return rtree;
}

function getConnectedRoofPerimeterRails(
  roofPerimeterRail: RoofPerimeterRail,
  rtree: RBush3D,
  isEnd: boolean,
  filter?: ConnectedRoofPerimeterRailFilter,
  boxHalfSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
): RoofPerimeterRail[] {
  const connectedRails: RoofPerimeterRail[] = [];
  const { id, rail, subRoofId } = roofPerimeterRail;
  const [x, y, z] = isEnd ? polyLine3EndPoint(rail) : polyLine3StartPoint(rail);

  const bbox = aabb3ByTwoPoints(
    x - boxHalfSize,
    y - boxHalfSize,
    z - boxHalfSize,
    x + boxHalfSize,
    y + boxHalfSize,
    z + boxHalfSize,
  );

  const results = rtree.search(bbox);

  for (const { roofPerimeterRail: otherRoofPerimeterRail, isEnd: isOtherEnd } of results) {
    // discard the same roof rail
    if (subRoofId === otherRoofPerimeterRail.subRoofId && id === otherRoofPerimeterRail.id) {
      continue;
    }

    // filtering out overlapping rails
    if (isEnd === isOtherEnd) {
      continue;
    }

    // apply filter
    if (filter && !filter(roofPerimeterRail, otherRoofPerimeterRail, isEnd, isOtherEnd)) {
      continue;
    }

    // add to connected rails
    connectedRails.push(otherRoofPerimeterRail);
  }

  return connectedRails.sort((a, b) => polyLine3Length(b.rail) - polyLine3Length(a.rail));
}

function isRoofPerimeterRailConnected(
  roofPerimeterRail: RoofPerimeterRail,
  rtree: RBush3D,
  isEnd: boolean,
  filter?: ConnectedRoofPerimeterRailFilter,
  boxHalfSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
): boolean {
  const connectedRails = getConnectedRoofPerimeterRails(roofPerimeterRail, rtree, isEnd, filter, boxHalfSize);

  return connectedRails.length > 0;
}

function connectedToDifferentRoofPred(
  roofPerimeterRail: RoofPerimeterRail,
  otherRoofPerimeterRail: RoofPerimeterRail,
  _isEnd: boolean,
  _isOtherEnd: boolean,
): boolean {
  return roofPerimeterRail.subRoofId !== otherRoofPerimeterRail.subRoofId;
}

export function isRoofPerimeterRailConnectedAtStart(
  roofPerimeterRail: RoofPerimeterRail,
  rtree: RBush3D,
  boxHalfSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
): boolean {
  return isRoofPerimeterRailConnected(roofPerimeterRail, rtree, false, connectedToDifferentRoofPred, boxHalfSize);
}

export function isRoofPerimeterRailConnectedAtEnd(
  roofPerimeterRail: RoofPerimeterRail,
  rtree: RBush3D,
  boxHalfSize: number = ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
): boolean {
  return isRoofPerimeterRailConnected(roofPerimeterRail, rtree, true, connectedToDifferentRoofPred, boxHalfSize);
}

interface GetRoofPerimeterWallRailOptions {
  readonly roofPerimeterRail: RoofPerimeterRail;
  readonly rtree: RBush3D;
  readonly roofTopFaces: RoofFace[];
  readonly getExteriorOffset?: (roofPerimeterRail: RoofPerimeterRail) => number;
  readonly ignoreClipping?: boolean;
}

export function getRoofPerimeterWallRail({
  roofPerimeterRail,
  rtree,
  roofTopFaces,
  getExteriorOffset = (roofPerimeterRail: RoofPerimeterRail) => roofPerimeterRail.exteriorWallOffsetWithFinish,
  ignoreClipping = false,
}: GetRoofPerimeterWallRailOptions): RoofPerimeterWallRail {
  const offsets: number[] = [];

  const { rail, overhang, wallLimitsRail } = roofPerimeterRail;
  const exteriorOffset = getExteriorOffset(roofPerimeterRail);
  let prevExteriorOffset = exteriorOffset;
  let nextExteriorOffset = exteriorOffset;

  const { otherPerimeterRail: prevPerimeterRail, type: startCornerType } = getRoofPerimeterCornerInfo(
    roofPerimeterRail,
    rtree,
    false,
    false,
  );
  const { otherPerimeterRail: nextPerimeterRail, type: endCornerType } = getRoofPerimeterCornerInfo(
    roofPerimeterRail,
    rtree,
    true,
    false,
  );

  // build the full rail (includes the previous and next rails if they exist)
  let fullRail = polyLine3New(0);
  let startVertex = 0;
  let endVertex = 0;
  let clipStart = true;
  let clipEnd = true;

  // concat the previous rail if it exists
  if (prevPerimeterRail?.rail) {
    const prevEdge = polyLine3ExtendStart(
      polyLine3Edge(prevPerimeterRail.rail, polyLine3EdgeCount(prevPerimeterRail.rail) - 1),
      overhang,
    );
    const [x, y, z] = polyLine3StartPoint(prevEdge);

    fullRail = polyLine3Concat([x, y, z], fullRail);

    startVertex = fullRail.length;

    const prevOffset = getExteriorOffset(prevPerimeterRail);
    offsets.push(-prevPerimeterRail.overhang + prevOffset);

    prevExteriorOffset = prevOffset;
    clipStart = startCornerType !== RoofRailCornerType.COPLANAR;
  }

  // concat the current rail
  fullRail = polyLine3Concat(fullRail, rail);
  const edgeCount = rail ? polyLine3EdgeCount(rail) : 0;
  if (edgeCount > 0) {
    offsets.push(...new Array<number>(edgeCount).fill(-overhang + exteriorOffset));
  }
  endVertex = fullRail.length;

  // concat the next rail if it exists
  if (nextPerimeterRail?.rail) {
    const nextEdge = polyLine3ExtendEnd(polyLine3Edge(nextPerimeterRail.rail, 0), overhang);
    const [x, y, z] = polyLine3EndPoint(nextEdge);

    fullRail = polyLine3Concat(fullRail, [x, y, z]);

    const nextOffset = getExteriorOffset(nextPerimeterRail);
    offsets.push(-nextPerimeterRail.overhang + nextOffset);

    nextExteriorOffset = nextOffset;
    clipEnd = endCornerType !== RoofRailCornerType.COPLANAR;
  }

  // offset the full rail
  fullRail = offsetPolyLine3VariableInXYPlane(fullRail, offsets);

  // build the top rail (extracts the current rail from the full rail)
  let wallRail = polyLine3New(endVertex - startVertex);
  wallRail.set(fullRail.slice(startVertex, endVertex));

  // clamp height
  for (let i = 0, l = wallRail.length; i < l; i += 3) {
    wallRail[i + 2] = getRoofHeightAtPoint(roofTopFaces, wallRail[i], wallRail[i + 1], wallRail[i + 2]);
  }

  // clip the inner rail to the wall limits rail
  const [tx, ty] = polyLine2EdgeTangent(wallLimitsRail, 0);
  const planes: PlaneBSP[] = [];

  if (!ignoreClipping && clipStart) {
    const [sx, sy] = polyLine2Start(wallLimitsRail);
    planes.push(planeBSPMoveAlongNormal(planeBSPByPointAndNormal(sx, sy, 0, -tx, -ty, 0), prevExteriorOffset));
  }

  if (!ignoreClipping && clipEnd) {
    const [ex, ey] = polyLine2End(wallLimitsRail);
    planes.push(planeBSPMoveAlongNormal(planeBSPByPointAndNormal(ex, ey, 0, tx, ty, 0), nextExteriorOffset));
  }

  if (planes.length > 0) {
    const bsp = addConvexPlanesToBSPLeaf(planes);
    const clippedRails = clipRail3({
      rail: wallRail,
      options: {
        clippingNodes: [clippingNodeNew(bsp, true)],
        epsilon: MULTIGABLE_BSP_EPSILON,
      },
    });

    wallRail = clippedRails.length > 0 ? clippedRails[0] : polyLine2New(0);
  }

  return roofPerimeterWallRailNew(
    wallRail,
    fullRail,
    startCornerType,
    endCornerType,
    prevPerimeterRail,
    nextPerimeterRail,
  );
}

export function getRoofPerimeterRailStartNormal({ normals }: RoofPerimeterRail): Vec3 {
  return normals[0];
}

export function getRoofPerimeterRailEndNormal({ normals }: RoofPerimeterRail): Vec3 {
  return normals[normals.length - 1];
}

export function getRoofPerimeterRailStartRafterDir({ rafterDirections }: RoofPerimeterRail): Vec3 {
  return rafterDirections[0];
}

export function getRoofPerimeterRailEndRafterDir({ rafterDirections }: RoofPerimeterRail): Vec3 {
  return rafterDirections[rafterDirections.length - 1];
}

export function getRoofCornerInfo(
  roofPerimeterRail: RoofPerimeterRail,
  isEnd: boolean,
  isAngledTrim: boolean,
): RoofRailConerInfo {
  const otherRoofPerimeterRail = isEnd ? roofPerimeterRail.nextPerimeterRail : roofPerimeterRail.prevPerimeterRail;
  const roofPerimeterRail1 = isEnd ? roofPerimeterRail : otherRoofPerimeterRail;
  const roofPerimeterRail2 = isEnd ? otherRoofPerimeterRail : roofPerimeterRail;

  if (!roofPerimeterRail1 || !roofPerimeterRail2) {
    return roofRailCornerInfoNew(RoofRailCornerType.NONE);
  }

  assertIsNotNullOrUndefined(otherRoofPerimeterRail);
  const { rail: otherRail } = otherRoofPerimeterRail;

  const { type: type1, eaveCutType: eaveCutType1, rail: rail1, edgePlane: edgePlane1 } = roofPerimeterRail1;
  const { type: type2, eaveCutType: eaveCutType2, rail: rail2, edgePlane: edgePlane2 } = roofPerimeterRail2;

  const endNormal = getRoofPerimeterRailEndNormal(roofPerimeterRail1);
  const startNormal = getRoofPerimeterRailStartNormal(roofPerimeterRail2);

  const [x0, y0] = polyLine3StartPoint(rail1);
  const [x1, y1] = polyLine3EndPoint(rail1);
  const [x2, y2] = polyLine3StartPoint(rail2);
  const [x3, y3] = polyLine3EndPoint(rail2);

  let plane1 = edgePlane1;
  let plane2 = edgePlane2;

  if (isAngledTrim && type1 === RoofPerimeterRailType.EAVE && eaveCutType1 === EaveCutType.SQUARED) {
    const { uX, uY, uZ } = plane1;
    plane1 = planeNew(rail1[0], rail1[1], rail1[2], uX, uY, uZ, endNormal[0], endNormal[1], endNormal[2]);
  }

  if (isAngledTrim && type2 === RoofPerimeterRailType.EAVE && eaveCutType2 === EaveCutType.SQUARED) {
    const { uX, uY, uZ } = plane2;
    plane2 = planeNew(rail2[0], rail2[1], rail2[2], uX, uY, uZ, startNormal[0], startNormal[1], startNormal[2]);
  }

  const [normal1X, normal1Y, normal1Z] = planeNormal(plane1);
  const [normal2X, normal2Y, normal2Z] = planeNormal(plane2);

  let [uX, uY, uZ] = vec3Add(normal1X, normal1Y, normal1Z, normal2X, normal2Y, normal2Z);
  [uX, uY, uZ] = vec3Normalize(uX, uY, uZ);

  let [vX, vY, vZ] = planeIsParallel(plane1, plane2)
    ? [plane1.vX, plane1.vY, plane1.vZ]
    : vec3Cross(normal1X, normal1Y, normal1Z, normal2X, normal2Y, normal2Z);
  [vX, vY, vZ] = vec3Normalize(vX, vY, vZ);

  if (vZ < 0) {
    [vX, vY, vZ] = vec3Negate(vX, vY, vZ);
  }

  const miterPlane = planeNew(x1, y1, 0, uX, uY, uZ, vX, vY, vZ);
  const angle = vec3AngleBetween(normal1X, normal1Y, normal1Z, normal2X, normal2Y, normal2Z);

  if (isEnd) {
    planeReverse(miterPlane);
  }

  // special case for almost collinear rails
  if (line2Collinear(x0, y0, x1, y1, x2, y2, x3, y3, DIST_EPSILON, ROOF_RAIL_COLLINEARITY_TOLERANCE)) {
    return roofRailCornerInfoNew(RoofRailCornerType.COPLANAR, otherRail, miterPlane, otherRoofPerimeterRail, angle);
  }

  const dir = line2Side(x0, y0, x1, y1, x3, y3);
  switch (dir) {
    case Line2Side.LEFT:
      return roofRailCornerInfoNew(RoofRailCornerType.OUTSIDE, otherRail, miterPlane, otherRoofPerimeterRail, angle);
    case Line2Side.RIGHT:
      return roofRailCornerInfoNew(RoofRailCornerType.INSIDE, otherRail, miterPlane, otherRoofPerimeterRail, angle);
    default:
      return roofRailCornerInfoNew(RoofRailCornerType.COPLANAR, otherRail, miterPlane, otherRoofPerimeterRail, angle);
  }
}

const CONNECTED_RAIL_END_TOLERANCE = 0.001; //keep it loose to avoid issues with floating point precision

export function perimeterRailsAreConnectedAndLieInParallelPlanes(
  roofPerimeterRail1: RoofPerimeterRail,
  roofPerimeterRail2: RoofPerimeterRail,
) {
  const { rail: rail1, edgePlane: edgePlane1 } = roofPerimeterRail1;
  const { rail: rail2, edgePlane: edgePlane2 } = roofPerimeterRail2;

  if (!rail1 || !rail2) {
    return false;
  }

  const [x0, y0, z0] = polyLine3StartPoint(rail1);
  const [x1, y1, z1] = polyLine3EndPoint(rail1);
  const [x2, y2, z2] = polyLine3StartPoint(rail2);
  const [x3, y3, z3] = polyLine3EndPoint(rail2);

  const l1 = vec3DistSquared(x0, y0, z0, x2, y2, z2);
  const l2 = vec3DistSquared(x0, y0, z0, x3, y3, z3);
  const l3 = vec3DistSquared(x1, y1, z1, x2, y2, z2);
  const l4 = vec3DistSquared(x1, y1, z1, x3, y3, z3);

  if (
    l1 < CONNECTED_RAIL_END_TOLERANCE ||
    l2 < CONNECTED_RAIL_END_TOLERANCE ||
    l3 < CONNECTED_RAIL_END_TOLERANCE ||
    l4 < CONNECTED_RAIL_END_TOLERANCE
  ) {
    return planeIsParallel(edgePlane1, edgePlane2);
  }
  return false;
}

export function getRoofPerimeterCornerInfo(
  roofPerimeterRail: RoofPerimeterRail,
  rtree: RBush3D,
  isEnd: boolean,
  isAngledTrim: boolean,
): RoofRailConerInfo {
  const connectedRails = getConnectedRoofPerimeterRails(roofPerimeterRail, rtree, isEnd);

  const { rail } = roofPerimeterRail;

  for (const otherRoofPerimeterRail of connectedRails) {
    const { rail: otherRail } = otherRoofPerimeterRail;
    const roofPerimeterRail1 = isEnd ? roofPerimeterRail : otherRoofPerimeterRail;
    const roofPerimeterRail2 = isEnd ? otherRoofPerimeterRail : roofPerimeterRail;
    const rail1 = isEnd ? rail : otherRail;
    const rail2 = isEnd ? otherRail : rail;

    const { edgePlane: edgePlane1, type: type1, eaveCutType: eaveCutType1 } = roofPerimeterRail1;
    const { edgePlane: edgePlane2, type: type2, eaveCutType: eaveCutType2 } = roofPerimeterRail2;

    const endNormal = getRoofPerimeterRailEndNormal(roofPerimeterRail1);
    const startNormal = getRoofPerimeterRailStartNormal(roofPerimeterRail2);

    const [x0, y0] = polyLine3StartPoint(rail1);
    const [x1, y1, z1] = polyLine3EndPoint(rail1);
    const [x2, y2] = polyLine3StartPoint(rail2);
    const [x3, y3] = polyLine3EndPoint(rail2);

    let plane1 = edgePlane1;
    let plane2 = edgePlane2;

    if (isAngledTrim && type1 === RoofPerimeterRailType.EAVE && eaveCutType1 === EaveCutType.SQUARED) {
      const { uX, uY, uZ } = plane1;
      plane1 = planeNew(rail1[0], rail1[1], rail1[2], uX, uY, uZ, endNormal[0], endNormal[1], endNormal[2]);
    }

    if (isAngledTrim && type2 === RoofPerimeterRailType.EAVE && eaveCutType2 === EaveCutType.SQUARED) {
      const { uX, uY, uZ } = plane2;
      plane2 = planeNew(rail2[0], rail2[1], rail2[2], uX, uY, uZ, startNormal[0], startNormal[1], startNormal[2]);
    }

    const [normal1X, normal1Y, normal1Z] = planeNormal(plane1);
    const [normal2X, normal2Y, normal2Z] = planeNormal(plane2);

    let [uX, uY, uZ] = vec3Add(normal1X, normal1Y, normal1Z, normal2X, normal2Y, normal2Z);
    [uX, uY, uZ] = vec3Normalize(uX, uY, uZ);
    let [vX, vY, vZ] = planeIsParallel(plane1, plane2)
      ? [plane1.vX, plane1.vY, plane1.vZ]
      : vec3Cross(normal1X, normal1Y, normal1Z, normal2X, normal2Y, normal2Z);
    [vX, vY, vZ] = vec3Normalize(vX, vY, vZ);

    if (vZ < 0) {
      [vX, vY, vZ] = vec3Negate(vX, vY, vZ);
    }

    const miterPlane = planeNew(x1, y1, z1, uX, uY, uZ, vX, vY, vZ);
    const angle = vec3AngleBetween(normal1X, normal1Y, normal1Z, normal2X, normal2Y, normal2Z);

    if (isEnd) {
      planeReverse(miterPlane);
    }

    // special case for almost collinear rails
    if (line2Collinear(x0, y0, x1, y1, x2, y2, x3, y3, DIST_EPSILON, ROOF_RAIL_COLLINEARITY_TOLERANCE)) {
      return roofRailCornerInfoNew(RoofRailCornerType.COPLANAR, otherRail, miterPlane, otherRoofPerimeterRail, angle);
    }

    const dir = line2Side(x0, y0, x1, y1, x3, y3);
    switch (dir) {
      case Line2Side.LEFT:
        return roofRailCornerInfoNew(RoofRailCornerType.OUTSIDE, otherRail, miterPlane, otherRoofPerimeterRail, angle);
      case Line2Side.RIGHT:
        return roofRailCornerInfoNew(RoofRailCornerType.INSIDE, otherRail, miterPlane, otherRoofPerimeterRail, angle);
      default:
        return roofRailCornerInfoNew(RoofRailCornerType.COPLANAR, otherRail, miterPlane, otherRoofPerimeterRail, angle);
    }
  }

  return roofRailCornerInfoNew(RoofRailCornerType.NONE);
}

function buildRoofPerimeterRailNormals(rail: PolyLine3, roofTopFaces: RoofFace[]): Vec3[] {
  const normals: Vec3[] = [];
  const edgeCount = rail ? polyLine3EdgeCount(rail) : 0;

  for (let i = 0; i < edgeCount; i++) {
    const [testX, testY] = polyLine3EvalEdge(rail, i, 0.5, true);

    normals.push(getRoofNormalAtPoint(roofTopFaces, testX, testY));
  }

  return normals;
}

function buildRoofPerimeterRailRafterDirections(rail: PolyLine3, normals: Vec3[], peakRail: PolyLine3): Vec3[] {
  const rafterDirections: Vec3[] = [];

  const [refTX, refTY, refTZ] = polyLine3EdgeTangent(peakRail, 0);

  const edgeCount = rail ? polyLine3EdgeCount(rail) : 0;

  for (let i = 0; i < edgeCount; i++) {
    const [tX, tY, tZ] = polyLine3EdgeTangent(rail, i);
    const normal = normals[i];

    const dot = vec3Dot(refTX, refTY, refTZ, tX, tY, tZ);
    if (Math.abs(dot) < MULTIGABLE_BSP_EPSILON) {
      rafterDirections.push(vec3Copy(vec3Cross(tX, tY, tZ, normal[0], normal[1], normal[2])));
    } else {
      const sign = Math.sign(dot);
      rafterDirections.push(
        vec3Copy(vec3Cross(sign * refTX, sign * refTY, sign * refTZ, normal[0], normal[1], normal[2])),
      );
    }
  }

  return rafterDirections;
}

export function isRoofRailCorner(roofRailCornerType: RoofRailCornerType): boolean {
  return roofRailCornerType !== RoofRailCornerType.NONE && roofRailCornerType !== RoofRailCornerType.COPLANAR;
}
